import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Button
} from '@mui/material';
import { 
  ShoppingBag, 
  Favorite, 
  LocationOn, 
  Person, 
  Logout, 
  CardGiftcard,
  Dashboard as DashboardIcon,
  LocalShipping,
} from '@mui/icons-material';
import { useUser } from '../../../context/UserContext';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import ProfileOverview from './ProfileOverview';

const AccountDashboard = () => {
  const { user, logout } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const rawRoles = user?.roles || user?.role || [];
  const userRolesList = Array.isArray(rawRoles)
    ? rawRoles
    : typeof rawRoles === "string"
      ? rawRoles.split(",").map(r => r.trim())
      : [];
  const isStaff = userRolesList.some(r => ["admin", "super admin"].includes(String(r).trim().toLowerCase())) ||
    (user?.email && ["arinfotechsolutions38@gmail.com", "sales@arinfotechsolutions.com", "admin@arinfotechsolutions.com", "admin@arinfotech.com"].includes(user.email.toLowerCase()));

  const isDashLoggedOut = localStorage.getItem('ar_dashboard_logged_out') === 'true';
  const dashboardPath = isDashLoggedOut ? '/dashboard/login' : '/dashboard';

  const menuItems = [
    ...(isStaff ? [{ label: 'Admin Dashboard', icon: <DashboardIcon sx={{ color: '#1E40AF' }} />, path: dashboardPath }] : []),
    { label: 'My Profile', icon: <Person />, path: '/account' },
    { label: 'Order History', icon: <ShoppingBag />, path: '/account/orders' },
    { label: 'Wishlist', icon: <Favorite />, path: '/account/wishlist' },
    { label: 'Addresses', icon: <LocationOn />, path: '/account/address' },
    { label: 'Track Order', icon: <LocalShipping />, path: '/account/track' },
  ];

  if (!user) return (
    <Container sx={{ py: 15, textAlign: 'center' }}>
      <Typography variant="h5">Please login to view your account.</Typography>
      <Button variant="contained" component={Link} to="/login" sx={{ mt: 3 }}>Login</Button>
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 0, borderRadius: 0, border: '1px solid #eee' }} elevation={0}>
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#F9FAF4' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{user.name || 'User'}</Typography>
              <Typography variant="body2" color="text.secondary">{user.email}</Typography>
              
              <Box sx={{ mt: 3, p: 2, bgcolor: '#2D6A4F', color: '#FFF' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, letterSpacing: 1.5 }}>LOYALTY POINTS</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{user.loyaltyPoints || 0}</Typography>
              </Box>

              {isStaff && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate(dashboardPath)}
                  startIcon={<DashboardIcon />}
                  sx={{
                    mt: 2.5,
                    bgcolor: '#1E40AF',
                    color: '#FFF',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 1.5,
                    py: 1.2,
                    boxShadow: '0 4px 12px rgba(30,64,175,0.25)',
                    '&:hover': { bgcolor: '#172554' }
                  }}
                >
                  Open Admin Dashboard
                </Button>
              )}
            </Box>
            <List sx={{ py: 2 }}>
              {menuItems.map((item) => (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton 
                    component={Link} 
                    to={item.path} 
                    selected={location.pathname === item.path}
                    sx={{
                      '&.Mui-selected': { 
                        bgcolor: 'rgba(45, 106, 79, 0.08)',
                        color: '#2D6A4F',
                        borderRight: '3px solid #2D6A4F',
                        '& .MuiListItemIcon-root': { color: '#2D6A4F' }
                      }
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
                  </ListItemButton>
                </ListItem>
              ))}
              <Divider sx={{ my: 1 }} />
              <ListItem disablePadding>
                <ListItemButton onClick={() => logout('/login')} sx={{ color: 'error.main' }}>
                  <ListItemIcon><Logout color="error" /></ListItemIcon>
                  <ListItemText primary="Log Out" primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Content Area */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 4, borderRadius: 0, minHeight: '500px', border: '1px solid #eee' }} elevation={0}>
            {/* The Outlet renders the child routes defined in App.js */}
            <Outlet context={{ user }} />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AccountDashboard;
